import { useEffect, useRef, useState } from 'react';
import { formatShortName, formatTime } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { ImagePlus } from 'lucide-react';
import { Banner, Button, Dialog, Icon, Skeleton, useToast } from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import { ApiError, errorMessage } from '../../../lib/api';
import { useDeleteAvatar, useProfile, useSaveSignature, useSignature } from '../../onboarding/user/api';
import type { Profile, Signature, SignatureBody } from '../../onboarding/user/types';
import { SettingsPageHeader, useSavedPill } from '../SettingsFrame';
import { CroppedAvatar, useAvatarFlow } from './avatarUpload';
import {
  ProfileFieldInput,
  ProfileInput,
  VIDEO_LINK_FIELDS,
  useProfileForm,
  type FieldDensity,
  type ProfileForm,
} from './ProfileFields';
import { SignatureBlock, SignatureEditor, bodyOf } from './SignatureEditor';
import styles from './ProfileSettingsPage.module.css';

const DESCRIPTION =
  'Diese Angaben erscheinen in Ihrer Signatur, auf Terminbestätigungen und im Mandantenportal.';

const VIDEO_LINK_HELP = 'Persönlicher Link, z. B. https://meet.google.com/abc-defg-hij';

/** The three personal meeting links (static Google Meet / Teams / Zoom channels), saved per field on blur. */
function VideoLinkFields({ form, density }: { form: ProfileForm; density: FieldDensity }) {
  return (
    <>
      {VIDEO_LINK_FIELDS.map((field) => (
        <ProfileFieldInput key={field} form={form} field={field} source="settings" density={density} />
      ))}
    </>
  );
}

/** `/settings/profile` — Einstellungen 01 (auto-save), 02 (crop dialog), 03 (failed save), EM2 on the phone. */
export function ProfileSettingsPage() {
  const mobile = useIsMobile();
  const { markSaved } = useSavedPill();
  const profile = useProfile();
  const form = useProfileForm(profile.data, 'settings', markSaved);

  if (profile.isError) {
    return (
      <div className={styles.page}>
        {mobile ? null : (
          <SettingsPageHeader eyebrow="Meine Einstellungen" title="Mein Profil" description={DESCRIPTION} />
        )}
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void profile.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(profile.error)}
        </Banner>
      </div>
    );
  }

  if (!profile.data) {
    return (
      <div className={styles.page} aria-busy="true">
        {mobile ? null : (
          <SettingsPageHeader eyebrow="Meine Einstellungen" title="Mein Profil" description={DESCRIPTION} />
        )}
        <Skeleton variant="block" height={mobile ? 150 : 158} />
        <Skeleton variant="block" height={mobile ? 330 : 320} />
        <Skeleton variant="block" height={mobile ? 70 : 260} />
      </div>
    );
  }

  return mobile ? (
    <MobileProfile profile={profile.data} form={form} onSaved={markSaved} />
  ) : (
    <DesktopProfile profile={profile.data} form={form} onSaved={markSaved} />
  );
}

interface ViewProps {
  profile: Profile;
  form: ProfileForm;
  onSaved: () => void;
}

/** E03: „Die Angaben konnten nicht gespeichert werden." with „Erneut versuchen". */
function SaveFailedBanner({ form }: { form: ProfileForm }) {
  return (
    <div className={styles.failed} role="alert">
      <span className={styles.failedTitle}>Die Angaben konnten nicht gespeichert werden.</span>
      <span className={styles.failedText}>Bitte prüfen Sie die markierten Felder.</span>
      <button
        type="button"
        className={styles.failedRetry}
        disabled={form.saving}
        onClick={() => void form.commitAll()}
      >
        Erneut versuchen
      </button>
    </div>
  );
}

function DesktopProfile({ profile, form, onSaved }: ViewProps) {
  return (
    <div className={styles.page}>
      {form.failed ? (
        <SaveFailedBanner form={form} />
      ) : (
        <SettingsPageHeader eyebrow="Meine Einstellungen" title="Mein Profil" description={DESCRIPTION} />
      )}
      {form.failed ? null : <AvatarCard profile={profile} onSaved={onSaved} />}
      <section className={styles.card} aria-labelledby="profile-fields-title">
        <h2 id="profile-fields-title" className={styles.cardTitle}>
          Persönliche Angaben
        </h2>
        <div className={clsx(styles.fieldGrid, form.failed && styles.fieldGridFailed)}>
          <ProfileFieldInput form={form} field="firstName" source="settings" density="settings" />
          <ProfileFieldInput form={form} field="lastName" source="settings" density="settings" />
          <ProfileFieldInput form={form} field="jobTitle" source="settings" density="settings" />
          <ProfileInput
            label="E-Mail-Adresse"
            density="settings"
            value={profile.email}
            readOnly
            note="Login-Adresse"
          />
          <ProfileFieldInput form={form} field="mobilePhone" source="settings" density="settings" />
          <ProfileFieldInput form={form} field="extension" source="settings" density="settings" />
        </div>
        {form.failed ? <SaveBar profile={profile} form={form} /> : null}
      </section>
      <section className={styles.card} aria-labelledby="profile-video-links-title">
        <div className={styles.cardTitleRow}>
          <h2 id="profile-video-links-title" className={styles.cardTitle}>
            Videokonferenz-Links
          </h2>
          <span className={styles.cardTitleNote}>{VIDEO_LINK_HELP}</span>
        </div>
        <div className={clsx(styles.fieldGrid, form.failed && styles.fieldGridFailed)}>
          <VideoLinkFields form={form} density="settings" />
        </div>
      </section>
      {form.failed ? null : <SignatureCard onSaved={onSaved} />}
    </div>
  );
}

/** E03 bottom bar: „Letzter erfolgreicher Stand: 14:28" · „Änderungen verwerfen" · „Speichern". */
function SaveBar({ profile, form }: { profile: Profile; form: ProfileForm }) {
  return (
    <div className={styles.saveBar}>
      <span className={styles.saveBarText}>Letzter erfolgreicher Stand: {formatTime(profile.savedAt)}</span>
      <span className={styles.saveBarActions}>
        <button type="button" className={styles.secondary} onClick={form.reset}>
          Änderungen verwerfen
        </button>
        <button
          type="button"
          className={styles.primary}
          disabled={form.problemCount > 0 || form.saving}
          onClick={() => void form.commitAll()}
        >
          Speichern
        </button>
      </span>
    </div>
  );
}

function AvatarPlaceholder({ size, label }: { size: number; label: string }) {
  return (
    <span className={styles.avatarPlaceholder} style={{ width: size, height: size }}>
      <Icon icon={ImagePlus} size={size > 100 ? 24 : 20} />
      <span className={styles.avatarPlaceholderText}>{label}</span>
    </span>
  );
}

function AvatarCard({ profile, onSaved }: { profile: Profile; onSaved: () => void }) {
  const flow = useAvatarFlow(onSaved);
  const remove = useDeleteAvatar();
  const inputRef = useRef<HTMLInputElement>(null);
  const shortName = formatShortName(profile.firstName, profile.lastName);
  return (
    <section className={styles.avatarCard} aria-labelledby="avatar-title">
      {profile.avatar ? (
        <CroppedAvatar
          url={profile.avatar.url}
          crop={profile.avatar.crop}
          size={112}
          className={styles.avatar}
          alt={`Profilbild ${shortName}`}
        />
      ) : (
        <AvatarPlaceholder size={112} label={`Profilbild ${shortName}`} />
      )}
      <div className={styles.avatarText}>
        <h2 id="avatar-title" className={styles.cardTitle}>
          Profilbild
        </h2>
        <span className={styles.avatarRule}>
          JPG oder PNG, mindestens 400 × 400 px. Wird automatisch quadratisch zugeschnitten.
        </span>
        {flow.error ? <span className={styles.avatarError}>{flow.error}</span> : null}
        {remove.isError ? <span className={styles.avatarError}>{errorMessage(remove.error)}</span> : null}
        <div className={styles.buttonRow}>
          <button type="button" className={styles.secondary} onClick={() => inputRef.current?.click()}>
            {profile.avatar ? 'Bild ersetzen' : 'Bild wählen'}
          </button>
          {profile.avatar ? (
            <button
              type="button"
              className={styles.ghostDanger}
              disabled={remove.isPending}
              onClick={() => remove.mutate(undefined, { onSuccess: onSaved })}
            >
              Entfernen
            </button>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          className={styles.fileInput}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) flow.choose(file);
          }}
        />
      </div>
      {flow.dialog}
    </section>
  );
}

function SignatureCard({ onSaved }: { onSaved: () => void }) {
  const signature = useSignature();
  const save = useSaveSignature();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);

  if (signature.isError) {
    return (
      <Banner
        tone="danger"
        actions={
          <Button variant="secondary" size="small" onClick={() => void signature.refetch()}>
            Erneut laden
          </Button>
        }
      >
        {errorMessage(signature.error)}
      </Banner>
    );
  }

  const data = signature.data;

  function fromTemplate(current: Signature) {
    save.mutate(
      { ...bodyOf(current), mode: 'template' },
      {
        onSuccess: onSaved,
        onError: (error) => toast({ message: errorMessage(error) }),
      },
    );
  }

  return (
    <section className={styles.card} aria-labelledby="signature-title">
      <div className={styles.cardTitleRow}>
        <h2 id="signature-title" className={styles.cardTitle}>
          E-Mail-Signatur
        </h2>
        <span className={styles.cardTitleNote}>wird an jede aus DAMOKLES gesendete Mail angehängt</span>
      </div>
      {data ? (
        <SignatureBlock lines={data.preview.lines} footer={data.preview.footer} variant="card" />
      ) : (
        <Skeleton variant="block" height={118} />
      )}
      <div className={styles.buttonRow}>
        <button type="button" className={styles.secondary} disabled={!data} onClick={() => setEditing(true)}>
          Signatur bearbeiten
        </button>
        <button
          type="button"
          className={styles.secondaryQuiet}
          disabled={!data || data.mode !== 'free' || save.isPending}
          onClick={() => data && fromTemplate(data)}
        >
          Aus Vorlage übernehmen
        </button>
      </div>
      {data ? (
        <SignatureDialog
          open={editing}
          signature={data}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onSaved();
          }}
        />
      ) : null}
    </section>
  );
}

/** „Signatur bearbeiten": the editor of Nutzer 09 in a dialog (E01 has no frame for it — own title). */
export function SignatureDialog({
  open,
  signature,
  onClose,
  onSaved,
}: {
  open: boolean;
  signature: Signature;
  onClose: () => void;
  onSaved: () => void;
}) {
  const mobile = useIsMobile();
  const save = useSaveSignature();
  const [value, setValue] = useState<SignatureBody>(() => bodyOf(signature));

  useEffect(() => {
    if (open) {
      setValue(bodyOf(signature));
      save.reset();
    }
    // Re-initialise on every opening only.
  }, [open]);

  const fieldError = save.error ? (fieldOf(save.error) ?? null) : null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Mein Profil"
      title="E-Mail-Signatur"
      width={620}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate(value, { onSuccess: onSaved })}>
            Speichern
          </Button>
        </>
      }
    >
      <SignatureEditor
        signature={signature}
        value={value}
        onChange={setValue}
        variant={mobile ? 'mobile' : 'dialog'}
        error={fieldError}
      />
      {save.isError && !fieldError ? <Banner tone="danger">{errorMessage(save.error)}</Banner> : null}
    </Dialog>
  );
}

function fieldOf(error: unknown): string | undefined {
  return error instanceof ApiError ? error.fields['text'] : undefined;
}

/** EM2: avatar card with „Foto aufnehmen"/„Aus Galerie", the field list, the signature row. */
function MobileProfile({ profile, form, onSaved }: ViewProps) {
  const flow = useAvatarFlow(onSaved);
  const signature = useSignature();
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const lineCount = signature.data ? signature.data.preview.lines.length : null;

  const pick = (file: File | undefined) => {
    if (file) flow.choose(file);
  };

  return (
    <div className={styles.mobilePage}>
      {form.failed ? <SaveFailedBanner form={form} /> : null}
      <section className={styles.mobileAvatarCard} aria-label="Profilbild">
        {profile.avatar ? (
          <CroppedAvatar
            url={profile.avatar.url}
            crop={profile.avatar.crop}
            size={86}
            alt={`Profilbild ${formatShortName(profile.firstName, profile.lastName)}`}
          />
        ) : (
          <AvatarPlaceholder size={86} label="Profilbild" />
        )}
        {flow.error ? <span className={styles.avatarError}>{flow.error}</span> : null}
        <div className={styles.mobileAvatarButtons}>
          <button type="button" className={styles.mobileButton} onClick={() => cameraRef.current?.click()}>
            Foto aufnehmen
          </button>
          <button type="button" className={styles.mobileButton} onClick={() => galleryRef.current?.click()}>
            Aus Galerie
          </button>
        </div>
        <input
          ref={cameraRef}
          type="file"
          accept="image/jpeg,image/png"
          capture="user"
          className={styles.fileInput}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            pick(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/jpeg,image/png"
          className={styles.fileInput}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            pick(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        {flow.dialog}
      </section>
      <section className={styles.mobileList} aria-label="Persönliche Angaben">
        <ProfileFieldInput form={form} field="firstName" source="settings" density="mobile" />
        <ProfileFieldInput form={form} field="lastName" source="settings" density="mobile" />
        <ProfileFieldInput form={form} field="mobilePhone" source="settings" density="mobile" />
        <ProfileFieldInput form={form} field="extension" source="settings" density="mobile" />
        <ProfileInput label="E-Mail · Login-Adresse" density="mobile" value={profile.email} readOnly />
      </section>
      <section className={styles.mobileSection} aria-labelledby="profile-video-links-mobile">
        <h2 id="profile-video-links-mobile" className={styles.mobileSectionTitle}>
          Videokonferenz-Links
        </h2>
        <div className={styles.mobileList}>
          <VideoLinkFields form={form} density="mobile" />
        </div>
        <span className={styles.mobileSectionHelp}>{VIDEO_LINK_HELP}</span>
      </section>
      {form.failed ? (
        <div className={styles.mobileSaveBar}>
          <span className={styles.saveBarText}>
            Letzter erfolgreicher Stand: {formatTime(profile.savedAt)}
          </span>
          <div className={styles.mobileSaveButtons}>
            <button type="button" className={styles.mobileButton} onClick={form.reset}>
              Änderungen verwerfen
            </button>
            <button
              type="button"
              className={clsx(styles.mobileButton, styles.mobilePrimary)}
              disabled={form.problemCount > 0 || form.saving}
              onClick={() => void form.commitAll()}
            >
              Speichern
            </button>
          </div>
        </div>
      ) : null}
      <button
        type="button"
        className={styles.mobileRow}
        disabled={!signature.data}
        onClick={() => setEditing(true)}
      >
        <span className={styles.mobileRowText}>
          <span className={styles.mobileRowTitle}>E-Mail-Signatur</span>
          <span className={styles.mobileRowSub}>
            {lineCount === null ? '' : `${lineCount} ${lineCount === 1 ? 'Zeile' : 'Zeilen'} hinterlegt`}
          </span>
        </span>
        <span className={styles.chevron} aria-hidden="true">
          ›
        </span>
      </button>
      {signature.data ? (
        <SignatureDialog
          open={editing}
          signature={signature.data}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onSaved();
          }}
        />
      ) : null}
    </div>
  );
}
